/**
 * ImageCarousel.js
 * 
 * Carousel per scorrere le immagini delle auto
 * Con frecce di navigazione e indicatori
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';

// `thumbs` (opzionale): miniature in parallelo a `images` (stesso ordine, vedi car.immaginiThumb). La striscia
// di anteprime sotto la foto grande mostra immagini alte 64px: usare le miniature (~100 KB) invece delle foto
// intere evita di scaricare N foto grandi appena si apre il dettaglio del veicolo. Se mancano (auto vecchie)
// si usa la foto intera come prima.
const ImageCarousel = ({ images = [], thumbs = [], title = "Foto Auto" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Se non ci sono immagini, mostra placeholder
  if (!images || images.length === 0) {
    return (
      <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Nessuna foto disponibile</p>
        </div>
      </div>
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="w-full">
      {/* Immagine principale */}
      <div className="relative w-full h-56 sm:h-72 md:h-96 bg-gray-100 rounded-lg overflow-hidden group">
        <img
          src={images[currentIndex]}
          alt={`${title} - Foto ${currentIndex + 1}`}
          className="w-full h-full object-contain"
          loading="lazy"
          decoding="async"
        />

        {/* Indicatore immagine corrente */}
        <div className="absolute top-4 right-4 bg-black bg-opacity-60 px-3 py-1 rounded-full text-white text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Pulsante precedente (sempre visibile, più marcato al passaggio del mouse) */}
        {images.length > 1 && (
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 p-2 rounded-full shadow-lg transition opacity-70 group-hover:opacity-100 focus:opacity-100 z-10"
            title="Foto precedente"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Pulsante successivo (sempre visibile, più marcato al passaggio del mouse) */}
        {images.length > 1 && (
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 p-2 rounded-full shadow-lg transition opacity-70 group-hover:opacity-100 focus:opacity-100 z-10"
            title="Foto successiva"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Thumbnail/Indicatori (solo se più di 1 foto) */}
      {images.length > 1 && (
        <div className="mt-4">
          {/* Versione desktop: Thumbnail */}
          <div className="hidden sm:grid grid-cols-4 md:grid-cols-6 gap-2">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`relative h-16 rounded-lg overflow-hidden border-2 transition ${
                  index === currentIndex
                    ? 'border-blue-500 shadow-lg'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                title={`Foto ${index + 1}`}
              >
                <img
                  src={(Array.isArray(thumbs) && thumbs[index]) || image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
                {index === currentIndex && (
                  <div className="absolute inset-0 bg-blue-500 bg-opacity-20"></div>
                )}
              </button>
            ))}
          </div>

          {/* Versione mobile: Dot indicators */}
          <div className="sm:hidden flex justify-center gap-2 mt-4">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition ${
                  index === currentIndex
                    ? 'w-6 bg-blue-600'
                    : 'w-2 bg-gray-400 hover:bg-gray-500'
                }`}
                title={`Foto ${index + 1}`}
              ></button>
            ))}
          </div>
        </div>
      )}

      {/* Informazioni */}
      {images.length > 0 && (
        <p className="text-xs text-gray-500 mt-2 text-center">
          {images.length === 1
            ? '1 foto'
            : `${images.length} foto - Usa le frecce per scorrere`}
        </p>
      )}
    </div>
  );
};

export default ImageCarousel;
